import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'hp-color-scheme';

const ColorSchemeContext = createContext(null);

function readSystemDark() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ColorSchemeProvider({ children }) {
  const [pref, setPrefState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'system';
    } catch {
      return 'system';
    }
  });
  const [systemDark, setSystemDark] = useState(readSystemDark);
  const [marketingLight, setMarketingLightState] = useState(false);

  const setMarketingLight = useCallback((value) => {
    setMarketingLightState(!!value);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved = useMemo(() => {
    if (marketingLight) return 'light';
    if (pref === 'system') return systemDark ? 'dark' : 'light';
    return pref;
  }, [marketingLight, pref, systemDark]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-color-scheme', resolved);
  }, [resolved]);

  const setPref = useCallback((next) => {
    setPrefState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({
      pref,
      setPref,
      resolved,
      /** Force le thème clair (pages marketing) tout en conservant la préférence utilisateur. */
      setMarketingLight,
    }),
    [pref, resolved, setPref, setMarketingLight],
  );

  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

export function useColorScheme() {
  const ctx = useContext(ColorSchemeContext);
  if (!ctx) {
    throw new Error('useColorScheme doit être utilisé sous ColorSchemeProvider');
  }
  return ctx;
}
